/**
 * AuthContext — application-wide Cognito auth state.
 *
 * Responsibilities:
 *  - Restore the Cognito session on mount (so page reload keeps the user logged in).
 *  - Listen to Amplify Hub events (signedIn / signedOut / tokenRefresh) and keep
 *    state in sync.
 *  - After a successful sign-in, call the Django "sync" endpoint to create/fetch the
 *    Django User and apply stored theme + language preferences.
 *  - Expose helpers: signIn (→ Hosted UI), signOut, updateUserPreferences.
 *  - Provide pre-built Cognito Hosted UI URLs for signup and forgot-password flows.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Hub } from 'aws-amplify/utils'
import {
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
  signOut as amplifySignOut,
  deleteUser,
} from '@aws-amplify/auth'

import i18n from '../i18n/index'
import { syncUser, updatePreferences } from '../api/userApi'
import { signUpWithRedirect } from '../auth/signUpWithRedirect'
import { setAuthToken } from '../api/apiClient'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  username: string
  userId: string
}

export interface AuthContextValue {
  /** The currently authenticated Cognito user, or null if not signed in. */
  user: AuthUser | null
  /** True while the initial session restoration is in progress. */
  isLoading: boolean
  /** The raw Cognito access token string, for attaching to API requests. */
  accessToken: string | null
  /** Triggers Cognito Hosted UI login/signup via signInWithRedirect (PKCE). */
  signIn: () => void
  /** Opens the human-check modal; on success redirects to Cognito Hosted UI for registration. */
  register: () => void
  /** Signs the user out via Amplify. */
  signOut: () => Promise<void>
  /** Permanently deletes the Cognito account and clears local auth state. */
  deleteAccount: () => Promise<void>
  /**
   * Persist theme / language / font_size / city_slug / news_language changes to Django.
   * Also updates the live theme/i18n/font-size immediately on the calling side.
   */
  updateUserPreferences: (prefs: {
    theme?: string
    language?: string
    font_size?: string
    city_slug?: string
    news_language?: string
  }) => Promise<void>
  /** Whether the human-check (Turnstile) modal is open. */
  isHumanCheckOpen: boolean
  /** Close the human-check modal without proceeding. */
  closeHumanCheck: () => void
  /** Called by HumanCheckModal after successful verification — proceeds to Cognito. */
  onHumanCheckSuccess: () => void
  /** True while a background Cognito token refresh is in progress. */
  isRefreshing: boolean
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isHumanCheckOpen, setIsHumanCheckOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Keep the module-level token store in sync so authFetch() always has
  // the current token without needing to thread it through every call site.
  useEffect(() => {
    setAuthToken(accessToken)
  }, [accessToken])

  // ---------------------------------------------------------------------------
  // Apply server-side preferences locally.
  // ---------------------------------------------------------------------------

  function applyPreferences(
    theme: string,
    language: string,
    fontSizeRaw?: string,
    citySlug?: string,
    newsLanguage?: string,
  ) {
    // Notify the useTheme hook via a custom event so its state stays in sync.
    window.dispatchEvent(
      new CustomEvent('radiri:theme-change', { detail: { theme } })
    )
    localStorage.setItem('radiri-theme', theme)
    void i18n.changeLanguage(language)
    if (fontSizeRaw === 'small' || fontSizeRaw === 'medium' || fontSizeRaw === 'large') {
      window.dispatchEvent(
        new CustomEvent('radiri:font-size-change', { detail: { fontSize: fontSizeRaw } })
      )
      localStorage.setItem('radiri-font-size', fontSizeRaw)
    }
    if (citySlug && newsLanguage) {
      try {
        localStorage.setItem(
          'radiri-last-selection',
          JSON.stringify({ lang: newsLanguage, citySlug }),
        )
      } catch {
        // localStorage unavailable — non-fatal
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Session helpers.
  // ---------------------------------------------------------------------------

  /** Fetch the current Cognito session, update state, and sync with Django. */
  const loadSession = useCallback(async () => {
    try {
      console.log('[Auth:loadSession] Fetching Cognito user and session...')
      const session = await fetchAuthSession()
      const token = session.tokens?.accessToken?.toString() ?? null
      console.log('[Auth:loadSession] Session tokens available:', {
        accessToken: !!token,
        idToken: !!session.tokens?.idToken,
        clockTimestamp: session.tokens?.accessToken?.['payload']?.['iat'],
      })

      // Synchronously update the module-level token so that any TanStack Query
      // fetches triggered on the same render cycle (before useEffect runs) already
      // have the correct token.  The useEffect below keeps it in sync for later
      // changes (e.g. token refresh), but this direct call closes the timing race
      // that causes the initial page-restore load to fail.
      setAuthToken(token)

      const cognitoUser = await getCurrentUser()
      console.log('[Auth:loadSession] Cognito user:', cognitoUser.username)
      
      setUser({ username: cognitoUser.username, userId: cognitoUser.userId })
      setAccessToken(token)

      if (token) {
        try {
          console.log('[Auth:loadSession] Syncing user with Django...')
          const prefs = await syncUser(token)
          console.log('[Auth:loadSession] Django sync successful, prefs:', prefs)
          applyPreferences(
            prefs.theme,
            prefs.language,
            prefs.font_size,
            prefs.city_slug || undefined,
            prefs.news_language || undefined,
          )
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err)
          console.error('[Auth:loadSession] Django sync failed (non-fatal):', errMsg)
          // Non-fatal — backend may not yet be configured or reachable.
        }
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err)
      console.log('[Auth:loadSession] No current session (normal for logged-out users):', errMsg)
      // No current session — normal for unauthenticated users.
      setAuthToken(null)
      setUser(null)
      setAccessToken(null)
    }
  }, [])

  // On mount: check for an existing stored session (returning visitor).
  // DO NOT call fetchAuthSession() or touch the code param here — Amplify's
  // configure() already handles the PKCE exchange internally.  Any attempt to
  // call fetchAuthSession() while the exchange is in progress will race and
  // return an empty session.  We rely on the Hub 'signedIn' event instead.
  useEffect(() => {
    loadSession().finally(() => setIsLoading(false))
  }, [loadSession])

  // Hub listener — covers the post-Hosted-UI redirect and sign-out.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('[Auth:Hub] Event:', payload.event)
      switch (payload.event) {
        case 'signedIn':
          console.log('[Auth:Hub] User signed in, loading session...')
          void loadSession()
          break
        case 'signedOut':
          console.log('[Auth:Hub] User signed out')
          setUser(null)
          setAccessToken(null)
          break
        case 'tokenRefresh':
          console.log('[Auth:Hub] Token refreshed')
          setIsRefreshing(true)
          void fetchAuthSession()
            .then((session) => {
              const token = session.tokens?.accessToken?.toString() ?? null
              setAuthToken(token)  // keep module-level store in sync immediately
              setAccessToken(token)
            })
            .finally(() => {
              setIsRefreshing(false)
            })
          break
        case 'signInWithRedirect_failure':
          // Strip the stale ?code= from the URL so configure() doesn't
          // try (and fail) to re-exchange the same code on the next load.
          console.error('[Auth:Hub] signInWithRedirect failed — full payload:', JSON.stringify(payload))
          window.history.replaceState({}, '', window.location.pathname)
          break
        default:
          break
      }
    })
    return unsubscribe
  }, [loadSession])

  // ---------------------------------------------------------------------------
  // Exposed actions.
  // ---------------------------------------------------------------------------

  const signIn = useCallback(() => {
    signInWithRedirect().catch((err) => console.error('signInWithRedirect:', err))
  }, [])

  const register = useCallback(() => {
    setIsHumanCheckOpen(true)
  }, [])

  const closeHumanCheck = useCallback(() => {
    setIsHumanCheckOpen(false)
  }, [])

  const onHumanCheckSuccess = useCallback(() => {
    setIsHumanCheckOpen(false)
    signUpWithRedirect().catch((err) => console.error('signUpWithRedirect:', err))
  }, [])

  const signOut = useCallback(async () => {
    await amplifySignOut()
  }, [])

  const deleteAccount = useCallback(async () => {
    await deleteUser()
    await amplifySignOut()
    setUser(null)
    setAccessToken(null)
  }, [])

  const updateUserPreferences = useCallback(
    async (prefs: { theme?: string; language?: string; font_size?: string; city_slug?: string; news_language?: string }) => {
      if (!accessToken) return
      const updated = await updatePreferences(accessToken, prefs)
      applyPreferences(
        updated.theme,
        updated.language,
        updated.font_size,
        updated.city_slug || undefined,
        updated.news_language || undefined,
      )
    },
    [accessToken]
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        accessToken,
        signIn,
        register,
        signOut,
        deleteAccount,
        updateUserPreferences,
        isHumanCheckOpen,
        closeHumanCheck,
        onHumanCheckSuccess,
        isRefreshing,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook — export both from here and from hooks/useAuth.ts for convenience.
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>')
  }
  return ctx
}
